import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../lib/api';

/**
 * The session list response shape from `GET /auth/sessions`. Mirrors
 * `crates/sentinel-web/src/routes/auth/sessions.rs::SessionResponse`.
 * `is_current` is computed server-side against the request's access-token
 * `session_id` claim — do NOT recompute it on the client.
 */
export interface SessionItem {
  id: string;
  user_id: string;
  device_info: {
    ip_address: string | null;
    user_agent: string | null;
  };
  login_method?: {
    method:
      | 'password' | 'mfa' | 'passkey' | 'passkey_mfa' | 'email_otp'
      | 'magic_link' | 'recovery_code' | 'sso' | 'impersonation' | 'device_code';
    idp_name?: string | null;
    idp_protocol?: string | null;
  } | null;
  created_at: string;
  last_active_at: string;
  expires_at: string;
  is_current: boolean;
}

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface SessionsError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): SessionsError {
  const e = new Error(stage) as SessionsError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

export const sessionsQueryKey = ['sessions', 'list'] as const;

/**
 * Fetches the authenticated user's active sessions. Uses the default backend
 * pagination limit which is plenty for v1.
 */
export function useSessions() {
  const api = useApi();
  return useQuery<SessionItem[], SessionsError>({
    queryKey: sessionsQueryKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/auth/sessions', {});
      if (error) throw toError('sessions_list_failed', response, error);
      const payload = data as { items?: unknown[] } | undefined;
      const items = (payload?.items ?? []) as SessionItem[];
      return items;
    },
  });
}
